
import React, { useState, useEffect } from 'react';
import { Poll } from '@/types/poll';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import PollOptions from '@/components/polls/shared/PollOptions';
import { useAuth } from '@/contexts/auth';
import { usePollSocialMetrics } from '@/hooks/usePollSocialMetrics';
import PollAccessIndicator from '@/components/polls/shared/poll-display/PollAccessIndicator';
import PollVotingStatus from '@/components/polls/shared/poll-display/PollVotingStatus';
import PollMetadata from '@/components/polls/shared/poll-display/PollMetadata';
import PollShareSection from '@/components/polls/shared/poll-display/PollShareSection';
import { useAnonymousVoting } from '@/components/polls/shared/poll-display/useAnonymousVoting';
import { PollDisplayProps } from '@/components/polls/shared/poll-display/types';
import PollVoteStats from '@/components/polls/shared/poll-display/PollVoteStats';
import { useVoteFiltering } from '@/hooks/polls/useVoteFiltering';
import { useCentralizedVoting } from '@/hooks/voting/useCentralizedVoting';
import { debugVoteFlow } from '@/utils/debug';
import TrendingBadge from '@/components/social/TrendingBadge';

const PollDisplay: React.FC<PollDisplayProps> = ({
  poll,
  hasVoted = false,
  canVote,
  cannotVoteReason,
  onVote,
  onRefresh,
  variant = 'default',
  showShareButtons = true,
  showLocation = true,
  showTrendingBadge = true
}) => {
  const { isAuthenticated } = useAuth();
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [hasVotedPersistent, setHasVotedPersistent] = useState(hasVoted);
  const [isLoadingVoteState, setIsLoadingVoteState] = useState(true);
  
  // Social metrics
  const { metrics: socialMetrics } = usePollSocialMetrics(poll?.id);

  // Centralized voting integration
  const { getUserSelectedOption, validateVote } = useCentralizedVoting();

  // Anonymous voting logic
  const {
    anonymousId,
    isAnonymousAllowed,
    hasAnonymousVoted,
    isCheckingAnonymousVote,
    updateAnonymousAllowed,
    submitAnonymousVote
  } = useAnonymousVoting(poll?.id, isAuthenticated);

  // Vote filtering logic with accurate breakdowns - now includes unverified and anonymized votes
  const {
    voteFilter,
    handleFilterChange,
    voteStats,
    filteredOptions,
    filteredTotalVotes,
    isLoadingBreakdown,
    error
  } = useVoteFiltering(poll);

  // Load user's existing vote when poll changes or authentication state changes
  useEffect(() => {
    const loadUserVoteState = async () => {
      if (!poll?.id) {
        setIsLoadingVoteState(false);
        return;
      }

      setIsLoadingVoteState(true);
      
      try {
        // Get user's selected option from centralized voting system
        const userSelectedOption = await getUserSelectedOption(poll.id);
        
        debugVoteFlow('POLL_DISPLAY_LOAD_VOTE_STATE', { 
          pollId: poll.id, 
          userSelectedOption,
          hasVoted,
          hasAnonymousVoted
        });

        if (userSelectedOption !== null) {
          setSelectedOption(userSelectedOption);
          setHasVotedPersistent(true);
        } else {
          // Check if poll results indicate user has voted
          if (poll.results?.selectedOptionId) {
            setSelectedOption(poll.results.selectedOptionId);
            setHasVotedPersistent(true);
          } else {
            setSelectedOption(null);
            setHasVotedPersistent(hasVoted || hasAnonymousVoted);
          }
        }
      } catch (error) {
        debugVoteFlow('POLL_DISPLAY_LOAD_VOTE_ERROR', { error });
        console.error('Error loading user vote state:', error);
        // Fallback to prop values
        setSelectedOption(poll?.results?.selectedOptionId || null);
        setHasVotedPersistent(hasVoted || hasAnonymousVoted);
      } finally {
        setIsLoadingVoteState(false);
      }
    };

    loadUserVoteState();
  }, [poll?.id, poll?.results?.selectedOptionId, hasVoted, hasAnonymousVoted, getUserSelectedOption, isAuthenticated]);

  // Update anonymous allowed status when poll changes
  useEffect(() => {
    if (poll?.id) {
      updateAnonymousAllowed(!!poll.anonymous_allowed);
    }
  }, [poll, updateAnonymousAllowed]);

  // Enhanced vote handler with proper state management
  const handleOptionClick = async (optionId: number) => {
    if (hasVotedPersistent) {
      toast.error('You have already voted on this poll');
      return;
    }

    if (!poll) return;

    // Validate vote eligibility before proceeding
    try {
      const canUserVote = await validateVote(poll.id);
      if (!canUserVote) {
        toast.error('You cannot vote on this poll at this time');
        return;
      }
    } catch (error) {
      debugVoteFlow('POLL_DISPLAY_VALIDATION_ERROR', { error });
      toast.error('Unable to validate vote. Please try again.');
      return;
    }

    if (isAuthenticated || (isAnonymousAllowed && anonymousId)) {
      try {
        debugVoteFlow('POLL_DISPLAY_VOTE_START', { pollId: poll.id, optionId });
        
        // Immediately set the selected option and mark as voted for instant UI feedback
        setSelectedOption(optionId);
        setHasVotedPersistent(true);
        
        // Call the parent's onVote handler - let it handle all the logic
        await onVote(optionId);
        
        debugVoteFlow('POLL_DISPLAY_VOTE_SUCCESS', { pollId: poll.id, optionId });
        
        // Refresh if available (after a short delay to allow real-time updates)
        if (onRefresh) {
          setTimeout(() => {
            onRefresh();
          }, 500);
        }
      } catch (error) {
        console.error('Error submitting vote:', error);
        
        // Revert UI state on error
        setSelectedOption(null);
        setHasVotedPersistent(hasVoted || hasAnonymousVoted);
        
        // Check if error indicates user already voted
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('already voted')) {
          // Re-load vote state to get correct selected option
          try {
            const userSelectedOption = await getUserSelectedOption(poll.id);
            if (userSelectedOption !== null) {
              setSelectedOption(userSelectedOption);
              setHasVotedPersistent(true);
            }
          } catch (loadError) {
            debugVoteFlow('POLL_DISPLAY_RELOAD_VOTE_ERROR', { loadError });
          }
          toast.error('You have already voted on this poll');
        } else {
          toast.error('Failed to submit vote. Please try again.');
        }
      }
    } else {
      toast.error(cannotVoteReason || 'You cannot vote on this poll');
    }
  };

  // Determine if the user can vote on this poll
  const userCanVote = isAuthenticated 
    ? canVote 
    : (isAnonymousAllowed && !hasAnonymousVoted && !isCheckingAnonymousVote);

  // Get the user's selected option ID - prioritize loaded state over prop values
  const userVoteOptionId = selectedOption || poll?.results?.selectedOptionId;

  if (!poll) return null;

  return (
    <div className="w-full">
      {/* Poll title and trending badge for embedded variant */}
      {poll.title && variant === 'embedded' && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-nation-blue dark:text-blue-300 flex-1">
            {poll.title}
          </h3>
          {showTrendingBadge && (
            <TrendingBadge 
              score={socialMetrics?.heatScore || 0}
              size="sm"
            />
          )}
        </div>
      )}
      
      {/* Trending badge for other variants */}
      {showTrendingBadge && variant !== 'embedded' && (
        <div className="flex justify-end mb-2">
          <TrendingBadge 
            score={socialMetrics?.heatScore || 0}
            size={variant === 'compact' ? 'sm' : 'md'}
          />
        </div>
      )}
      
      {/* Poll access indicator */}
      <PollAccessIndicator 
        isAnonymousAllowed={isAnonymousAllowed} 
        isPublicVotingAllowed={poll.public_voting_allowed}
      />

      {/* Vote status indicator - use persistent voting status */}
      <PollVotingStatus
        hasVoted={hasVotedPersistent && isAuthenticated}
        hasAnonymousVoted={hasVotedPersistent && !isAuthenticated}
      />
      
      {/* Vote statistics and filter - now includes unverified and anonymized votes */}
      <PollVoteStats
        totalVotes={voteStats.totalVotes}
        anonymousVotes={voteStats.anonymousVotes}
        verifiedVotes={voteStats.verifiedVotes}
        unverifiedVotes={voteStats.unverifiedVotes}
        anonymizedVotes={voteStats.anonymizedVotes}
        currentFilter={voteFilter}
        onFilterChange={handleFilterChange}
        className="mb-3"
        isLoading={isLoadingBreakdown}
        error={error}
      />
      
      {/* Show loading state while fetching vote status */}
      {isLoadingVoteState ? (
        <div className="flex items-center justify-center py-4">
          <div className="text-sm text-muted-foreground">Loading vote status...</div>
        </div>
      ) : (
        /* Unified poll options - always shows both options and results */
        <PollOptions 
          options={filteredOptions}
          onVote={handleOptionClick}
          selectedId={userVoteOptionId || null}
          hasVoted={hasVotedPersistent}
          canVote={userCanVote && !hasVotedPersistent}
          totalVotes={filteredTotalVotes}
          variant={variant === 'embedded' ? 'compact' : variant}
        />
      )}
      
      {/* Cannot vote message */}
      {!userCanVote && !hasVotedPersistent && !isLoadingVoteState && (
        <div className="flex items-center mt-3 text-sm text-amber-600">
          <AlertCircle size={14} className="mr-1" />
          {cannotVoteReason || "You cannot vote on this poll"}
        </div>
      )}

      {/* Poll metadata */}
      <PollMetadata 
        poll={poll}
        showLocation={showLocation}
        variant={variant}
      />

      {/* Share buttons */}
      {showShareButtons && (
        <PollShareSection 
          poll={poll}
          showShareButtons={true}
          variant={variant}
        />
      )}
    </div>
  );
};

export default PollDisplay;
